import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ScienceIcon from "@mui/icons-material/Science";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { downloadXlsx } from "../../utils/excelSheet";
import ConfirmDialog from "../../components/ConfirmDialog";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITIES } from "../../auth/permissions";

const CSV_COLUMNS = [
  { field: "preFinanceId", headerName: "PreFinanceId" },
  { field: "entityNumber", headerName: "EntityNumber" },
  { field: "entityName", headerName: "EntityName" },
  { field: "preFinanceType", headerName: "PreFinanceType" },
  { field: "preFinanceCode", headerName: "PreFinanceCode" },
  { field: "isLegacy", headerName: "IsLegacy" },
  { field: "payedOut", headerName: "PayedOut" },
  { field: "action", headerName: "Action" },
  { field: "skipReason", headerName: "SkipReason" },
  { field: "linkedInvoiceCount", headerName: "LinkedInvoiceCount" },
  { field: "linkedExciseClaimCount", headerName: "LinkedExciseClaimCount" },
  { field: "countryId", headerName: "PricingCountryId" },
  { field: "countryName", headerName: "PricingCountryName" },
  { field: "entityCountryId", headerName: "CustomerCountryId" },
  { field: "entityCountryName", headerName: "CustomerCountryName" },
  { field: "pricingAgreementId", headerName: "PricingAgreementId" },
  { field: "pricingPeriodType", headerName: "PricingPeriodType" },
  { field: "isNetherlandsEntity", headerName: "IsNetherlandsEntity" },
  { field: "appliedVfPercentage", headerName: "AppliedVfPercentage" },
  { field: "oldPayoutAmount", headerName: "OldPayoutAmount" },
  { field: "sourceComparisonAmount", headerName: "SourceComparisonAmount" },
  { field: "historicalPayoutDifference", headerName: "HistoricalPayoutDifference" },
  { field: "refundAmount", headerName: "RefundAmount" },
  { field: "declarationAmount", headerName: "DeclarationAmount" },
  { field: "refundFee", headerName: "RefundFee" },
  { field: "vatOnDeclarationFee", headerName: "VatOnDeclarationFee" },
  { field: "totalCosts", headerName: "TotalCosts" },
  { field: "newPayoutAmount", headerName: "NewPayoutAmount" },
  { field: "calculation", headerName: "Calculation", value: (item) => formatCalculation(item) },
  { field: "payoutDelta", headerName: "PayoutDelta" },
  { field: "payoutPreserved", headerName: "PayoutPreserved" },
];

const escapeCsvField = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  if (text.includes(";") || text.includes("\"") || text.includes("\n")) {
    return `"${text.replace(/"/g, "\"\"")}"`;
  }
  return text;
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return Number(value).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatCalculation = (item) => {
  if (
    !item ||
    item.refundAmount == null ||
    item.appliedVfPercentage == null ||
    item.refundFee == null ||
    item.totalCosts == null ||
    item.newPayoutAmount == null
  ) {
    return "";
  }

  const refund = formatMoney(item.refundAmount);
  const declaration = formatMoney(item.declarationAmount);
  const vf = formatMoney(item.appliedVfPercentage);
  const refundFee = formatMoney(item.refundFee);
  const vatFee = formatMoney(item.vatOnDeclarationFee);
  const costs = formatMoney(item.totalCosts);
  const payout = formatMoney(item.newPayoutAmount);
  const lines = [`${refund} × ${vf}% = ${refundFee}`];
  if (item.isNetherlandsEntity) {
    lines.push(`${declaration} × ${vf}% × 21% = ${vatFee}`);
    lines.push(`kosten ${refundFee} + ${vatFee} = ${costs}`);
  } else {
    lines.push(`kosten ${costs}`);
  }
  lines.push(`payout ${refund} − ${costs} = ${payout}`);
  return lines.join("\n");
};

const MISSING_PRICING_REASON = "MissingPricingAgreement";
const MISSING_VF_REASON = "MissingVfPercentage";
const INVALID_PRICING_REASON = "InvalidPricingAgreement";

const rowsForSkipReason = (report, reason) =>
  (report?.items || []).filter((item) => item.skipReason === reason);

const overviewColumns = ({ includeAgreementId = false, includePeriodType = false } = {}) => [
  { header: "PreFinanceId", width: 14, value: (item) => item.preFinanceId },
  { header: "CustomerNumber", width: 16, value: (item) => item.entityNumber || "" },
  { header: "CustomerName", width: 32, value: (item) => item.entityName || "" },
  { header: "Product", width: 12, value: (item) => item.preFinanceType || "" },
  { header: "CountryId", width: 12, value: (item) => item.countryId ?? "" },
  { header: "Country", width: 24, value: (item) => item.countryName || "" },
  { header: "PreFinanceCode", width: 22, value: (item) => item.preFinanceCode || "" },
  { header: "Legacy", width: 10, value: (item) => (item.isLegacy ? "Yes" : "No") },
  { header: "PaidOut", width: 10, value: (item) => item.payedOut },
  { header: "Invoices", width: 12, value: (item) => item.linkedInvoiceCount },
  { header: "ExciseClaims", width: 14, value: (item) => item.linkedExciseClaimCount },
  { header: "OldPayoutAmount", width: 18, value: (item) => item.oldPayoutAmount ?? "" },
  ...(includeAgreementId
    ? [{ header: "PricingAgreementId", width: 20, value: (item) => item.pricingAgreementId ?? "" }]
    : []),
  ...(includePeriodType
    ? [{ header: "PeriodType", width: 14, value: (item) => item.pricingPeriodType ?? "" }]
    : []),
];

const downloadSkipOverviewXlsx = async (items, columns, sheetName, fileName) => {
  const rows = items.map((item) =>
    columns.reduce((row, column) => {
      row[column.header] = column.value(item);
      return row;
    }, {})
  );
  await downloadXlsx(
    rows,
    sheetName,
    fileName,
    columns.map((column) => column.width)
  );
};

const downloadReportCsv = (report) => {
  const rows = [
    CSV_COLUMNS.map((column) => column.headerName).join(";"),
    ...(report.items || []).map((item) =>
      CSV_COLUMNS.map((column) => escapeCsvField(column.value ? column.value(item) : item[column.field])).join(";")
    ),
  ];
  const csv = `\uFEFF${rows.join("\n")}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const fileName = report.dryRun
    ? "prefinance-recalculation-dryrun.csv"
    : "prefinance-recalculation-report.csv";
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const SummaryCard = ({ label, value, color }) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" color={color || "text.primary"}>
        {value ?? 0}
      </Typography>
    </CardContent>
  </Card>
);

const PrefinanceHistoricalRecalculation = () => {
  const { hasPermission } = usePermissions();
  const canRecalculate = hasPermission(CAPABILITIES.adminMigrateLegacy, "X");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  const columns = useMemo(
    () => [
      { field: "preFinanceId", headerName: "Id", width: 80 },
      { field: "entityNumber", headerName: "Customer", width: 120 },
      { field: "entityName", headerName: "Name", width: 180 },
      { field: "preFinanceType", headerName: "Product", width: 90 },
      {
        field: "countryName",
        headerName: "Pricing country",
        width: 160,
        valueGetter: (params) => params.row.countryName || params.row.countryId || "",
      },
      {
        field: "entityCountryName",
        headerName: "Customer country",
        width: 170,
        valueGetter: (params) => params.row.entityCountryName || params.row.entityCountryId || "",
      },
      { field: "action", headerName: "Action", width: 190 },
      { field: "skipReason", headerName: "Skip reason", minWidth: 220 },
      { field: "payedOut", headerName: "Paid", width: 80 },
      {
        field: "appliedVfPercentage",
        headerName: "VF%",
        width: 80,
        valueFormatter: (params) => formatMoney(params.value),
      },
      {
        field: "oldPayoutAmount",
        headerName: "Old payout",
        width: 120,
        valueFormatter: (params) => formatMoney(params.value),
      },
      {
        field: "refundAmount",
        headerName: "Refund",
        width: 120,
        valueFormatter: (params) => formatMoney(params.value),
      },
      {
        field: "totalCosts",
        headerName: "Costs",
        width: 110,
        valueFormatter: (params) => formatMoney(params.value),
      },
      {
        field: "newPayoutAmount",
        headerName: "New payout",
        width: 120,
        valueFormatter: (params) => formatMoney(params.value),
      },
      {
        field: "calculation",
        headerName: "Calculation",
        flex: 1,
        minWidth: 280,
        sortable: false,
        cellClassName: "calculation-cell",
        valueGetter: (params) => formatCalculation(params.row),
        renderCell: (params) => (
          <Box
            component="span"
            sx={{
              whiteSpace: "pre-line",
              overflowWrap: "anywhere",
              lineHeight: 1.45,
              py: 0.75,
            }}
          >
            {params.value}
          </Box>
        ),
      },
      {
        field: "payoutDelta",
        headerName: "Delta",
        width: 110,
        valueFormatter: (params) => formatMoney(params.value),
      },
      { field: "payoutPreserved", headerName: "Payout kept", width: 110 },
      { field: "linkedInvoiceCount", headerName: "Invoices", width: 90 },
      { field: "linkedExciseClaimCount", headerName: "Excise", width: 90 },
    ],
    []
  );

  const handleRunRecalculation = async (dryRun) => {
    if (!canRecalculate) {
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post("/api/v1/preFinance/recalculateHistoricalAmounts", null, {
        params: { dryRun },
        timeout: 0,
      });
      setReport(response.data);
      setSuccess(
        dryRun
          ? "Dry-run completed. No records were changed."
          : "Recalculation completed. Refund and payout were updated only for the rows marked Updated."
      );
    } catch (requestError) {
      console.error("Prefinance historical recalculation failed:", requestError);
      setError(
        requestError.response?.data?.message ||
          requestError.response?.data ||
          "The prefinance recalculation could not be completed."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDryRun = () => {
    handleRunRecalculation(true);
  };

  const handleDownloadCsv = () => {
    if (!report) {
      return;
    }

    downloadReportCsv(report);
  };

  const missingPricingCount = rowsForSkipReason(report, MISSING_PRICING_REASON).length;
  const missingVfCount = rowsForSkipReason(report, MISSING_VF_REASON).length;
  const invalidPricingCount = rowsForSkipReason(report, INVALID_PRICING_REASON).length;

  const handleExportMissingPricing = async () => {
    if (!report || missingPricingCount === 0) {
      return;
    }

    await downloadSkipOverviewXlsx(
      rowsForSkipReason(report, MISSING_PRICING_REASON),
      overviewColumns(),
      "Missing pricing",
      "prefinance-missing-pricing-agreements.xlsx"
    );
  };

  const handleExportMissingVf = async () => {
    if (!report || missingVfCount === 0) {
      return;
    }

    await downloadSkipOverviewXlsx(
      rowsForSkipReason(report, MISSING_VF_REASON),
      overviewColumns({ includeAgreementId: true }),
      "Missing VF",
      "prefinance-missing-vf-percentage.xlsx"
    );
  };

  const handleExportInvalidPricing = async () => {
    if (!report || invalidPricingCount === 0) {
      return;
    }

    await downloadSkipOverviewXlsx(
      rowsForSkipReason(report, INVALID_PRICING_REASON),
      overviewColumns({ includeAgreementId: true, includePeriodType: true }),
      "Invalid pricing",
      "prefinance-invalid-pricing-agreements.xlsx"
    );
  };

  const handleExecute = () => {
    setConfirmDialog({
      isOpen: true,
      title: "Recalculate historical prefinance amounts",
      subTitle:
        "This writes Refund, costs and Payout on historical dossiers that the dry-run marked for update. Paid-out flags and payout dates stay unchanged. Dossiers whose old payout does not match the source sum are skipped. Continue?",
      onConfirm: () => {
        setConfirmDialog((currentDialog) => ({ ...currentDialog, isOpen: false }));
        handleRunRecalculation(false);
      },
    });
  };

  if (!canRecalculate) {
    return (
      <Alert severity="warning">The admin.migrateLegacy permission is required to recalculate historical prefinance amounts.</Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Prefinance recalculation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Historical dossiers stored the gross prefinance amount in Payout and left Refund empty. This job fills Refund
        from the linked invoices or Excise claims and recalculates Payout with the current pricing-agreement VF%.
        Start with a dry-run. Already calculated dossiers are skipped, so a second run does not change them again.
      </Typography>

      <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ScienceIcon />}
          onClick={handleDryRun}
          disabled={loading}
          aria-label="Run prefinance recalculation dry-run"
        >
          Run dry-run
        </Button>
        <Button
          variant="outlined"
          startIcon={<CloudDownloadIcon />}
          onClick={handleDownloadCsv}
          disabled={!report || loading}
          aria-label="Download prefinance recalculation CSV"
        >
          Download CSV
        </Button>
        <Button
          variant="outlined"
          startIcon={<SaveAltIcon />}
          onClick={handleExportMissingPricing}
          disabled={!report || loading || missingPricingCount === 0}
          aria-label="Export dossiers with a missing pricing agreement to Excel"
        >
          Export missing pricing ({missingPricingCount})
        </Button>
        <Button
          variant="outlined"
          startIcon={<SaveAltIcon />}
          onClick={handleExportMissingVf}
          disabled={!report || loading || missingVfCount === 0}
          aria-label="Export dossiers with a pricing agreement that has no VF percentage to Excel"
        >
          Export missing VF ({missingVfCount})
        </Button>
        <Button
          variant="outlined"
          startIcon={<SaveAltIcon />}
          onClick={handleExportInvalidPricing}
          disabled={!report || loading || invalidPricingCount === 0}
          aria-label="Export dossiers with a pricing agreement that has no valid period type to Excel"
        >
          Export invalid pricing ({invalidPricingCount})
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<PlayArrowIcon />}
          onClick={handleExecute}
          disabled={loading || !report?.dryRun}
          aria-label="Execute prefinance recalculation"
        >
          Execute recalculation
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : "The prefinance recalculation could not be completed."}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {report && (
        <>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Chip
              label={report.dryRun ? "Dry-run" : "Executed"}
              color={report.dryRun ? "info" : "success"}
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              {report.items?.length || 0} historical rows in this report
            </Typography>
          </Box>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Candidates" value={report.candidateCount} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Already calculated" value={report.alreadyCalculatedCount} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="To update" value={report.updatedCount} color="success.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Snapshot only" value={report.snapshotOnlyCount} color="info.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Skipped" value={report.skippedCount} color="warning.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Missing pricing" value={missingPricingCount} color="warning.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Missing VF" value={missingVfCount} color="warning.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <SummaryCard label="Invalid pricing" value={invalidPricingCount} color="warning.main" />
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <DataGrid
                autoHeight
                getRowHeight={() => "auto"}
                getRowId={(row) => row.preFinanceId}
                rows={report.items || []}
                columns={columns}
                density="compact"
                disableSelectionOnClick
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                components={{ Toolbar: GridToolbar }}
                componentsProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 250 },
                    csvOptions: { disableToolbarButton: true },
                    printOptions: { disableToolbarButton: true },
                  },
                }}
                sx={{
                  border: 0,
                  "& .MuiDataGrid-cell:hover": {
                    color: "primary.main",
                  },
                  "& .MuiDataGrid-cell.calculation-cell": {
                    whiteSpace: "pre-line",
                    alignItems: "flex-start",
                    lineHeight: 1.45,
                  },
                }}
              />
            </CardContent>
          </Card>
        </>
      )}

      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </Box>
  );
};

export default PrefinanceHistoricalRecalculation;
